/**
 * Photo Upload Block - JavaScript
 * 
 * Handles the multi-step modal for uploading images to be attached to cart items.
 * Images are stored as base64 data URLs in hidden inputs as line item properties.
 * 
 * @version 2.1.0 — Performance optimized
 * @compatible Horizon Theme (Shopify Online Store 2.0)
 */

class PhotoUploadComponent extends HTMLElement {
  constructor() {
    super();

    // State
    this.files = []; // Array of { file: File, dataUrl: string, id: string }
    this.currentStep = 'upload'; // 'upload' | 'preview'
    this.maxFiles = 9;
    this.maxFileSizeMB = 10;
    this.allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    this.isSubmitting = false;
    this._hiddenInputTimer = null; // Debounce timer for updateHiddenInputs

    // DOM references
    this.modal = null;
    this.trigger = null;
    this.fileInput = null;
    this.dropzone = null;
    this.previewGrid = null;
    this.countDisplay = null;

    // Bind methods ONCE in constructor only
    this.handleTriggerClick = this.handleTriggerClick.bind(this);
    this.handleFileSelect = this.handleFileSelect.bind(this);
    this.handleDragOver = this.handleDragOver.bind(this);
    this.handleDragLeave = this.handleDragLeave.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleModalClose = this.handleModalClose.bind(this);
    this.handleAddToCart = this.handleAddToCart.bind(this);
    this.handleActionButtonClick = this.handleActionButtonClick.bind(this);
    this.handleRemoveFile = this.handleRemoveFile.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    this.render();
    this.bindEvents();

    // Detect initial Package variant and set maxFiles accordingly
    this.detectPackageVariant();

    // Listen for variant changes to update maxFiles dynamically
    this.handleVariantUpdate = this.handleVariantUpdate.bind(this);
    document.addEventListener('variant:update', this.handleVariantUpdate);

    // Also listen for direct variant picker clicks (faster response)
    this.handleVariantClick = this.handleVariantClick.bind(this);
    document.addEventListener('variant:selected', this.handleVariantClick);

    // Check for existing files in hidden inputs (for edit mode)
    this.loadExistingFiles();
  }

  disconnectedCallback() {
    this.unbindEvents();
    document.removeEventListener('variant:update', this.handleVariantUpdate);
    document.removeEventListener('variant:selected', this.handleVariantClick);
  }

  /**
   * Main render - creates the trigger button and modal structure
   */
  render() {
    const productTitle = this.dataset.productTitle || 'Product';
    const buttonText = this.dataset.buttonText || 'Upload pictures';
    const requiredCount = parseInt(this.dataset.requiredCount, 10) || 1;

    this.maxFiles = parseInt(this.dataset.maxFiles, 10) || 9;

    this.innerHTML = `
      <button type="button" class="photo-upload-trigger" data-testid="photo-upload-trigger">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
        <span>${buttonText}</span>
      </button>
      
      <!-- Modal -->
      <div class="photo-upload-modal" data-testid="photo-upload-modal" role="dialog" aria-modal="true" aria-labelledby="photo-upload-title">
        <div class="photo-upload-modal__container">
          <header class="photo-upload-modal__header">
            <h2 class="photo-upload-modal__title" id="photo-upload-title">Upload images</h2>
            <button type="button" class="photo-upload-modal__close" aria-label="Close">&times;</button>
          </header>
          
          <div class="photo-upload-modal__body">
            <!-- Step 1: Upload Source -->
            <div class="photo-upload-step photo-upload-step--upload" data-step="upload">
              <div class="photo-upload-dropzone" data-testid="photo-upload-dropzone">
                <svg class="photo-upload-dropzone__icon" viewBox="0 0 64 64" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="8" y="12" width="36" height="36" rx="4"/>
                  <rect x="20" y="16" width="36" height="36" rx="4"/>
                  <circle cx="20" cy="28" r="4"/>
                  <path d="M8 40 l12 -12 l8 8 l12 -12 l4 4"/>
                </svg>
                <p class="photo-upload-dropzone__text">Drag & Drop Images</p>
                <button type="button" class="photo-upload-pc-btn" data-testid="photo-upload-pc-btn">
                  <span class="photo-upload-text-desktop">+ Upload From PC</span>
                  <span class="photo-upload-text-mobile">+ Upload From Gallery</span>
                </button>
              </div>
              
              <div class="photo-upload-sources">
                <p class="photo-upload-sources__label">or choose from</p>
                <div class="photo-upload-sources__icons">
                  <button type="button" class="photo-upload-source-icon" data-action="camera" title="Camera">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                      <circle cx="12" cy="13" r="4"/>
                    </svg>
                  </button>
                  <button type="button" class="photo-upload-source-icon" data-action="link" title="Upload bulk links">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/>
                      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>
                    </svg>
                  </button>
                </div>
              </div>
              
              <input 
                type="file" 
                class="photo-upload-input" 
                accept="image/jpeg,image/png,image/webp,image/gif" 
                multiple 
                data-testid="photo-upload-input"
              />
              <!-- Camera specific input -->
              <input 
                type="file" 
                class="photo-upload-camera-input" 
                accept="image/*" 
                capture="environment"
                multiple
                style="display: none;"
              />
            </div>
            
            <!-- Step 2: Preview -->
            <div class="photo-upload-step photo-upload-step--preview" data-step="preview" style="display: none;">
              <div class="photo-upload-preview">
                <div class="photo-upload-preview__grid-wrapper">
                  <div class="photo-upload-preview__grid" data-testid="photo-upload-grid">
                    <!-- Preview items will be inserted here -->
                  </div>
                  
                  <div class="photo-upload-preview__sidebar">
                    <div class="photo-upload-preview__info">
                      <h3>Product Preview</h3>
                      <div class="photo-upload-preview__note">
                        <span class="photo-upload-preview__note-icon"></span>
                        <span>Area beyond the dotted lines will wrap around the edges of your magnets</span>
                      </div>
                      <p class="photo-upload-preview__tip">Click the "Crop" icon near a picture to resize or crop it</p>
                      <p class="photo-upload-preview__count">
                        <strong>Selected</strong> <span data-count>0</span>/<span data-max-count>${this.maxFiles}</span>
                      </p>
                    </div>
                    
                    <div class="photo-upload-sidebar-buttons">
                      <button type="button" class="photo-upload-action-btn" data-testid="photo-upload-action">
                        <span class="photo-upload-action-btn__icon">+</span>
                        <span class="photo-upload-action-btn__text">ADD ${this.maxFiles} MORE</span>
                      </button>
                      <button type="button" class="photo-upload-addtocart-btn" data-testid="photo-upload-addtocart" style="display: none;">
                        ADD TO CART
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Hidden inputs for form submission -->
      <div class="photo-upload-hidden-inputs" style="display: none;" data-testid="photo-upload-hidden-inputs">
        <!-- Will contain hidden inputs with image data URLs -->
      </div>
    `;

    // Cache DOM references
    this.trigger = this.querySelector('.photo-upload-trigger');
    this.modal = this.querySelector('.photo-upload-modal');
    this.fileInput = this.querySelector('.photo-upload-input');
    this.dropzone = this.querySelector('.photo-upload-dropzone');
    this.previewGrid = this.querySelector('.photo-upload-preview__grid');
    this.countDisplay = this.querySelector('[data-count]');
    this.uploadStep = this.querySelector('[data-step="upload"]');
    this.previewStep = this.querySelector('[data-step="preview"]');
  }

  /**
   * Bind all event listeners
   * NOTE: Methods are already bound in constructor, do NOT re-bind here
   */
  bindEvents() {
    // Trigger
    this.trigger?.addEventListener('click', this.handleTriggerClick);

    // File Input
    this.fileInput?.addEventListener('change', this.handleFileSelect);

    // PC Upload Button
    this.modal.querySelector('.photo-upload-pc-btn')?.addEventListener('click', () => {
      this.fileInput?.click();
    });

    // Camera Input
    const cameraInput = this.modal.querySelector('.photo-upload-camera-input');
    this.modal.querySelector('[data-action="camera"]')?.addEventListener('click', () => {
      cameraInput?.click();
    });
    cameraInput?.addEventListener('change', (e) => {
      this.handleFileSelect(e);
      e.target.value = ''; // Reset to allow re-selection
    });

    // Bulk Link Upload
    this.modal.querySelector('[data-action="link"]')?.addEventListener('click', this.handleBulkLinkUpload.bind(this));

    // Drag & Drop
    ['dragenter', 'dragover', 'dragleave', 'drop'].forEach(eventName => {
      this.dropzone.addEventListener(eventName, (e) => {
        e.preventDefault();
        e.stopPropagation();
      });
    });
    this.dropzone.addEventListener('dragover', this.handleDragOver);
    this.dropzone.addEventListener('dragleave', this.handleDragLeave);
    this.dropzone.addEventListener('drop', this.handleDrop);

    // Modal Close
    this.querySelector('.photo-upload-modal__close')?.addEventListener('click', this.handleModalClose);
    this.modal?.addEventListener('click', (e) => {
      if (e.target === this.modal) this.handleModalClose();
    });
    document.addEventListener('keydown', this.handleKeyDown);

    // Action Button (Add More)
    this.modal.querySelector('.photo-upload-action-btn')?.addEventListener('click', this.handleActionButtonClick);

    // Add to Cart Button
    this.modal.querySelector('.photo-upload-addtocart-btn')?.addEventListener('click', this.handleAddToCart);

    // Form Intercept
    this.interceptFormSubmit();
  }

  /**
   * Handle Bulk Link Upload
   */
  async handleBulkLinkUpload() {
    const input = prompt("Enter image URLs (one per line):");
    if (!input) return;

    const urls = input.split(/[\n\r,]+/).map(u => u.trim()).filter(u => u);
    if (urls.length === 0) return;

    const validFiles = [];

    // Create a simple loading indicator
    const linkBtn = this.modal.querySelector('[data-action="link"]');
    const originalText = linkBtn.innerHTML;
    linkBtn.innerHTML = '...';

    for (const url of urls) {
      try {
        const response = await fetch(url);
        if (!response.ok) throw new Error('Network error');
        const blob = await response.blob();
        const filename = url.split('/').pop().split('?')[0] || `image-${Date.now()}.jpg`;
        const file = new File([blob], filename, { type: blob.type });

        if (file.type.startsWith('image/')) {
          validFiles.push(file);
        }
      } catch (err) {
        console.warn(`Failed to load image from ${url}:`, err);
      }
    }

    // Restore icon
    linkBtn.innerHTML = originalText;

    if (validFiles.length > 0) {
      this.processFiles(validFiles);
    } else {
      alert("Could not load images. Please ensure URLs are direct image links and accessible (CORS enabled).");
    }
  }

  /**
   * Intercept product form submit to inject photo inputs
   */
  interceptFormSubmit() {
    // Find the product form
    const productFormComponent = document.querySelector('product-form-component');
    const productForm = productFormComponent?.querySelector('form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!productForm) return;

    // Store reference for cleanup
    this.productForm = productForm;

    // Bind handler
    this.formSubmitHandler = (e) => {
      // Ensure our photo inputs are in the form before submission
      if (this.files && this.files.length > 0) {
        this.syncInputsToProductForm();
      }
    };

    // Use capture phase to run before other handlers
    productForm.addEventListener('submit', this.formSubmitHandler, { capture: true });
  }

  unbindEvents() {
    document.removeEventListener('keydown', this.handleKeyDown);

    // Clean up form submit listener
    if (this.productForm && this.formSubmitHandler) {
      this.productForm.removeEventListener('submit', this.formSubmitHandler, { capture: true });
    }
  }

  /**
   * Load existing files from hidden inputs (for edit mode)
   */
  loadExistingFiles() {
    // This can be extended to load previously uploaded files
    // For now, we start fresh each time the modal opens
  }

  /**
   * Detect initial Package variant and set maxFiles from it.
   * Looks for variant picker inputs with labels like "6 items", "9 items", etc.
   */
  detectPackageVariant() {
    // Find checked/selected Package option from variant picker
    const variantPicker = document.querySelector('variant-picker');
    if (!variantPicker) return;

    // Look for fieldsets — the Package option
    const fieldsets = variantPicker.querySelectorAll('fieldset');
    for (const fieldset of fieldsets) {
      const legend = fieldset.querySelector('legend, .variant-picker__option-name');
      const legendText = legend?.textContent?.trim().toLowerCase() || '';
      if (!legendText.includes('package')) continue;

      // Find checked input
      const checkedInput = fieldset.querySelector('input:checked');
      if (checkedInput) {
        const label = fieldset.querySelector(`label[for="${checkedInput.id}"]`);
        const value = label?.textContent?.trim() || checkedInput.value;
        const num = this.extractNumberFromVariant(value);
        if (num) {
          this.updateMaxFiles(num);
        }
      }
      break;
    }
  }

  /**
   * Handle variant:update event dispatched by VariantPicker after section re-render.
   * Re-detect Package variant and sync variant ID from re-rendered DOM.
   */
  handleVariantUpdate(event) {
    // After DOM morph, re-detect package value from the updated variant picker
    setTimeout(() => {
      this.detectPackageVariant();
      this.syncVariantId();
    }, 100);
  }

  /**
   * Handle variant:selected event for immediate feedback before section re-render.
   */
  handleVariantClick(event) {
    // Re-detect after a small delay (input state may update slightly after event)
    setTimeout(() => {
      this.detectPackageVariant();
      this.syncVariantId();
    }, 50);
  }

  /**
 * Sync data-variant-id attribute from the variant picker.
 * Ensures our component always has the correct variant ID after variant changes.
 *
 * Priority order:
 *   1. Variant picker checked input (theme-managed, always correct for current product)
 *   2. URL ?variant= param
 *   3. Keep existing data-variant-id (set by Liquid at page load)
 */
  syncVariantId() {
    // Priority 1: Variant picker checked input — most reliable, always this product
    const variantFromPicker = this.getVariantFromPicker();
    if (variantFromPicker) {
      this.dataset.variantId = variantFromPicker;
      console.log('[PhotoUpload] Synced variant from picker:', variantFromPicker);
      return;
    }

    // Priority 2: URL param
    const urlParams = new URLSearchParams(window.location.search);
    const urlVariant = urlParams.get('variant');
    if (urlVariant) {
      this.dataset.variantId = urlVariant;
      console.log('[PhotoUpload] Synced variant from URL:', urlVariant);
      return;
    }

    // Priority 3: Keep existing data-variant-id (Liquid rendered)
    // No action needed if it's already set from Liquid.
    // If it's not set, it will remain undefined, which is fine.
    console.log('[PhotoUpload] Using existing data-variant-id (fallback):', this.dataset.variantId);
  }

  /**
   * Read variant ID from the variant picker's checked inputs.
   * The variant picker is rendered by the theme for the CURRENT product only.
   * @returns {string|null}
   */
  getVariantFromPicker() {
    const section = this.closest('[data-section-id]') || this.closest('.shopify-section');
    const scope = section || document;

    const variantPicker = scope.querySelector('variant-picker');
    if (!variantPicker) return null;

    // Variant picker inputs have data-variant-id with the correct variant for each option
    const checkedInputs = variantPicker.querySelectorAll('input:checked[data-variant-id]');
    if (checkedInputs.length > 0) {
      // Use the last checked input's variant (most specific — e.g. Package option)
      return checkedInputs[checkedInputs.length - 1].dataset.variantId;
    }

    return null;
  }

  /**
   * Extract number from variant value string.
   * e.g., "6 items" -> 6, "9 items" -> 9, "12" -> 12
   */
  extractNumberFromVariant(value) {
    if (!value) return null;
    const match = value.match(/(\d+)/);
    return match ? parseInt(match[1], 10) : null;
  }

  /**
   * Update maxFiles and re-render UI accordingly.
   * If user already uploaded more photos than new max, trim excess with warning.
   */
  updateMaxFiles(newMax) {
    if (newMax === this.maxFiles) return;
    if (newMax < 1 || newMax > 50) return; // sanity check

    const oldMax = this.maxFiles;
    this.maxFiles = newMax;

    // Trim excess files if needed
    if (this.files.length > newMax) {
      const removed = this.files.length - newMax;
      this.files = this.files.slice(0, newMax);
      console.info(`Package changed: trimmed ${removed} excess photo(s) to fit ${newMax} max.`);
    }

    // Update grid columns dynamically
    this.updateGridLayout();

    // Update sidebar count display
    const maxCountEl = this.querySelector('[data-max-count]');
    if (maxCountEl) {
      maxCountEl.textContent = newMax;
    }

    // Re-render if preview is showing
    if (this.currentStep === 'preview' && this.files.length > 0) {
      this.renderPreviewGrid();
    }
    this.updateActionButton();
    this.debouncedUpdateHiddenInputs();
    this.updateTriggerButton();
  }

  /**
   * Update grid layout: always 3 columns, gap 10px.
   * Grid height is controlled by CSS flex layout, no JS maxHeight needed.
   */
  updateGridLayout() {
    if (!this.previewGrid) return;
    this.previewGrid.style.gridTemplateColumns = 'repeat(3, 1fr)';
    this.previewGrid.style.gap = '10px';
  }

  /**
   * Scroll the preview grid to show the last uploaded item
   */
  scrollToLastItem() {
    if (!this.previewGrid) return;
    requestAnimationFrame(() => {
      const items = this.previewGrid.querySelectorAll('.photo-upload-preview__item');
      if (items.length > 0) {
        const lastItem = items[items.length - 1];
        lastItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    });
  }

  /**
   * Open modal
   */
  handleTriggerClick() {
    this.modal?.classList.add('active');
    document.body.style.overflow = 'hidden';

    // Show appropriate step based on current files
    if (this.files.length > 0) {
      this.showStep('preview');
    } else {
      this.showStep('upload');
    }

    // Focus trap
    this.querySelector('.photo-upload-modal__close')?.focus();
  }

  /**
   * Close modal
   */
  handleModalClose() {
    this.modal?.classList.remove('active');
    document.body.style.overflow = '';
  }

  /**
   * Handle ESC key
   */
  handleKeyDown(e) {
    if (e.key === 'Escape' && this.modal?.classList.contains('active')) {
      this.handleModalClose();
    }
  }

  /**
   * Switch between upload and preview steps
   */
  showStep(step) {
    this.currentStep = step;

    if (step === 'upload') {
      this.uploadStep.style.display = '';
      this.previewStep.style.display = 'none';
    } else {
      this.uploadStep.style.display = 'none';
      this.previewStep.style.display = '';
      this.renderPreviewGrid();
    }
  }

  /**
   * Handle drag over
   */
  handleDragOver(e) {
    e.preventDefault();
    this.dropzone?.classList.add('drag-over');
  }

  /**
   * Handle drag leave
   */
  handleDragLeave(e) {
    e.preventDefault();
    this.dropzone?.classList.remove('drag-over');
  }

  /**
   * Handle file drop
   */
  handleDrop(e) {
    e.preventDefault();
    this.dropzone?.classList.remove('drag-over');

    const files = Array.from(e.dataTransfer?.files || []);
    this.processFiles(files);
  }

  /**
   * Handle file input change
   */
  handleFileSelect(e) {
    const files = Array.from(e.target?.files || []);
    this.processFiles(files);

    // Reset input to allow re-selecting same file
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  /**
   * Process and validate selected files
   */
  async processFiles(files) {
    const validFiles = [];

    for (const file of files) {
      // Check file type
      if (!this.allowedTypes.includes(file.type)) {
        console.warn(`Skipping ${file.name}: Invalid file type`);
        continue;
      }

      // Check file size
      const sizeMB = file.size / (1024 * 1024);
      if (sizeMB > this.maxFileSizeMB) {
        console.warn(`Skipping ${file.name}: File too large (${sizeMB.toFixed(2)}MB)`);
        continue;
      }

      // Check max files limit
      if (this.files.length + validFiles.length >= this.maxFiles) {
        if (validFiles.length === 0) {
          alert(`Maximum ${this.maxFiles} photos allowed. Please remove some photos before adding more.`);
        }
        break;
      }

      validFiles.push(file);
    }

    // Convert to data URLs in parallel for speed
    const results = await Promise.all(
      validFiles.map(file => this.fileToDataUrl(file).then(dataUrl => ({
        file,
        dataUrl,
        id: this.generateId()
      })))
    );
    this.files.push(...results);

    // Switch to preview if files added
    if (this.files.length > 0) {
      this.showStep('preview');
      this.debouncedUpdateHiddenInputs();
      this.updateTriggerButton();
      this.updateActionButton();
      this.scrollToLastItem();
    }
  }

  /**
   * Convert File to base64 data URL with auto-crop to 1:1 square.
   * 
   * Auto-crops from center to 1:1 ratio, then resizes to 800×800 at 85% JPEG.
   * This ensures thumbnails in the modal always show the cropped version.
   */
  fileToDataUrl(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const croppedDataUrl = this.autoCropSquare(img, 800, 0.85);
          resolve(croppedDataUrl);
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  /**
   * Auto-crop an image to 1:1 square (center crop) and resize.
   * @param {HTMLImageElement} img - The source image
   * @param {number} maxSize - Maximum width/height in pixels
   * @param {number} quality - JPEG quality (0.0 to 1.0)
   * @returns {string} Base64 data URL of the cropped square
   */
  autoCropSquare(img, maxSize, quality) {
    const w = img.width;
    const h = img.height;
    const cropSize = Math.min(w, h);
    const sx = (w - cropSize) / 2;
    const sy = (h - cropSize) / 2;

    const outputSize = Math.min(cropSize, maxSize);

    const canvas = document.createElement('canvas');
    canvas.width = outputSize;
    canvas.height = outputSize;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, outputSize, outputSize);
    return canvas.toDataURL('image/jpeg', quality);
  }

  /**
   * Compress an image to a target max dimension and JPEG quality.
   * Used both for initial upload and after cropping.
   * @param {HTMLImageElement} img - The source image
   * @param {number} maxSize - Maximum width/height in pixels
   * @param {number} quality - JPEG quality (0.0 to 1.0)
   * @returns {string} Base64 data URL
   */
  compressImage(img, maxSize, quality) {
    let width = img.width;
    let height = img.height;

    if (width > maxSize || height > maxSize) {
      const ratio = Math.min(maxSize / width, maxSize / height);
      width = Math.round(width * ratio);
      height = Math.round(height * ratio);
    }

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    return canvas.toDataURL('image/jpeg', quality);
  }

  /**
   * Re-compress a data URL for Shopify cart properties.
   * 400×400 @ 60% ≈ 8-12KB per image for sharp cart thumbnails.
   * @param {string} dataUrl - The source data URL to re-compress
   * @returns {Promise<string>} Base64 data URL (~8-12KB)
   */
  compressForCart(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(this.compressImage(img, 400, 0.6));
      };
      img.onerror = () => {
        resolve(dataUrl);
      };
      img.src = dataUrl;
    });
  }

  /**
   * Create a thumbnail for cart display and order detail in Shopify Admin.
   * 400×400 @ 60% ≈ 8-12KB per image. Sharp enough for cart preview grid.
   * @param {string} dataUrl - The source data URL
   * @returns {Promise<string>} Base64 data URL
   */
  compressForOrderThumbnail(dataUrl) {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        resolve(this.compressImage(img, 400, 0.6));
      };
      img.onerror = () => {
        resolve(dataUrl);
      };
      img.src = dataUrl;
    });
  }

  // ========================================
  // CLOUDFLARE WORKER INTEGRATION
  // ========================================

  /**
   * Get configured Cloudflare Worker URL and API key from data attributes.
   * @returns {{ url: string, key: string } | null}
   */
  getWorkerConfig() {
    const url = this.dataset.workerUrl?.trim();
    const key = this.dataset.workerKey?.trim();
    if (!url) return null;
    return { url: url.replace(/\/$/, ''), key: key || '' };
  }

  /**
   * Convert a base64 data URL to a Blob (for uploading cropped images).
   * @param {string} dataUrl - Base64 data URL
   * @returns {Blob}
   */
  dataUrlToBlob(dataUrl) {
    const [header, base64Data] = dataUrl.split(',');
    const mimeMatch = header.match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : 'image/jpeg';
    const binary = atob(base64Data);
    const array = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      array[i] = binary.charCodeAt(i);
    }
    return new Blob([array], { type: mime });
  }

  /**
   * Upload a single image to Cloudflare Worker R2.
   * Sends the image via FormData to POST /upload.
   * @param {File|Blob} fileOrBlob - The file/blob to upload
   * @param {string} filename - Filename for the upload
   * @param {{ url: string, key: string }} config - Worker config
   * @returns {Promise<string|null>} Public R2 URL or null on failure
   */
  async uploadToWorker(fileOrBlob, filename, config) {
    try {
      const formData = new FormData();
      formData.append('file', fileOrBlob, filename);

      const response = await fetch(`${config.url}/upload`, {
        method: 'POST',
        headers: { 'X-API-Key': config.key },
        body: formData
      });

      if (!response.ok) {
        const errText = await response.text();
        console.warn('[PhotoUpload] Worker upload error:', response.status, errText);
        return null;
      }

      const result = await response.json();
      if (result.url) {
        console.log('[PhotoUpload] Uploaded to Worker R2:', result.url);
        return result.url;
      }

      console.warn('[PhotoUpload] Worker unexpected response:', result);
      return null;
    } catch (err) {
      console.warn('[PhotoUpload] Worker upload failed:', err.message);
      return null;
    }
  }

  /**
   * Upload all photos to Cloudflare Worker with progress feedback.
   * Uploads the CROPPED version (item.dataUrl) not the original file.
   * Uses parallel batches of 3 for speed while avoiding overwhelming the server.
   * @param {HTMLElement} statusBtn - Button element to show progress
   * @param {{ url: string, key: string }} config - Worker config
   * @returns {Promise<Array<{index: number, url: string|null}>>}
   */
  async uploadAllToWorker(statusBtn, config) {
    const BATCH_SIZE = 3;
    const results = new Array(this.files.length);
    let completed = 0;

    for (let batchStart = 0; batchStart < this.files.length; batchStart += BATCH_SIZE) {
      const batchEnd = Math.min(batchStart + BATCH_SIZE, this.files.length);
      const batch = [];

      for (let i = batchStart; i < batchEnd; i++) {
        // Convert cropped dataUrl to Blob for upload
        const croppedBlob = this.dataUrlToBlob(this.files[i].dataUrl);
        const filename = this.files[i].file?.name || `photo-${i + 1}.jpg`;
        batch.push(
          this.uploadToWorker(croppedBlob, filename, config).then(url => {
            completed++;
            if (statusBtn) {
              statusBtn.textContent = `UPLOADING ${completed}/${this.files.length}...`;
            }
            results[i] = { index: i, url };
          })
        );
      }

      await Promise.all(batch);
    }

    return results;
  }

  /**
   * Generate a unique ID
   */
  generateId() {
    return 'photo_' + Math.random().toString(36).substr(2, 9);
  }

  /**
   * Update action button state based on file count
   * Shows "ADD X MORE" when < maxFiles, hides when full
   * The separate "ADD TO CART" button shows whenever files.length > 0
   */
  updateActionButton() {
    const actionBtn = this.querySelector('.photo-upload-action-btn');
    const addToCartBtn = this.querySelector('.photo-upload-addtocart-btn');
    if (!actionBtn) return;

    const iconSpan = actionBtn.querySelector('.photo-upload-action-btn__icon');
    const textSpan = actionBtn.querySelector('.photo-upload-action-btn__text');

    const remaining = this.maxFiles - this.files.length;

    if (remaining > 0) {
      // Still can add more photos - show add more button
      if (iconSpan) iconSpan.textContent = '+';
      if (textSpan) textSpan.textContent = `ADD ${remaining} MORE`;
      actionBtn.style.display = '';
    } else {
      // All slots filled - hide add more button
      actionBtn.style.display = 'none';
    }

    // Show ADD TO CART button only when all slots are filled
    if (addToCartBtn) {
      addToCartBtn.style.display = this.files.length >= this.maxFiles ? '' : 'none';
    }
  }

  /**
   * Handle action button click - always triggers file upload (add more)
   */
  handleActionButtonClick() {
    this.fileInput?.click();
  }

  /**
   * Render preview grid
   */
  renderPreviewGrid() {
    if (!this.previewGrid) return;

    let html = '';

    // Render existing files
    this.files.forEach((item, index) => {
      html += `
        <div class="photo-upload-preview__item" data-id="${item.id}">
          <img src="${item.dataUrl}" alt="Photo ${index + 1}" />
          <div class="photo-upload-preview__actions">
            <button type="button" class="photo-upload-preview__action-btn photo-upload-preview__action-btn--delete" data-action="delete" data-id="${item.id}" title="Delete">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
              </svg>
            </button>
            <button type="button" class="photo-upload-preview__action-btn" data-action="crop" data-id="${item.id}" title="Crop">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M6.13 1L6 16a2 2 0 0 0 2 2h15"/>
                <path d="M1 6.13L16 6a2 2 0 0 1 2 2v15"/>
              </svg>
            </button>
            <button type="button" class="photo-upload-preview__action-btn" data-action="duplicate" data-id="${item.id}" title="Duplicate">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
          </div>
        </div>
      `;
    });

    // Add "Add more" cell if under limit
    if (this.files.length < this.maxFiles) {
      html += `
        <div class="photo-upload-preview__add-more" data-testid="photo-upload-add-more">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="12" y1="5" x2="12" y2="19"/>
            <line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
          <span>Add more</span>
        </div>
      `;
    }

    this.previewGrid.innerHTML = html;

    // Apply grid layout based on current maxFiles
    this.updateGridLayout();

    // Update count display: "Selected X / Y"
    if (this.countDisplay) {
      this.countDisplay.textContent = this.files.length;
    }
    const maxCountEl = this.querySelector('[data-max-count]');
    if (maxCountEl) {
      maxCountEl.textContent = this.maxFiles;
    }

    // Bind delete buttons
    this.previewGrid.querySelectorAll('[data-action="delete"]').forEach(btn => {
      btn.addEventListener('click', this.handleRemoveFile);
    });

    // Bind crop buttons
    this.previewGrid.querySelectorAll('[data-action="crop"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.openCropModal(e.currentTarget.dataset.id));
    });

    // Bind duplicate buttons
    this.previewGrid.querySelectorAll('[data-action="duplicate"]').forEach(btn => {
      btn.addEventListener('click', (e) => this.handleDuplicateFile(e.currentTarget.dataset.id));
    });

    // Bind add more cell
    this.previewGrid.querySelector('.photo-upload-preview__add-more')?.addEventListener('click', () => {
      this.fileInput?.click();
    });
  }

  /**
   * Remove a file
   */
  handleRemoveFile(e) {
    const id = e.currentTarget.dataset.id;
    this.files = this.files.filter(f => f.id !== id);

    this.renderPreviewGrid();
    this.debouncedUpdateHiddenInputs();
    this.updateTriggerButton();
    this.updateActionButton();

    // Go back to upload step if no files
    if (this.files.length === 0) {
      this.showStep('upload');
    }
  }

  /**
   * Duplicate a file — clone and insert right after the original
   * @param {string} fileId - ID of the file to duplicate
   */
  handleDuplicateFile(fileId) {
    if (this.files.length >= this.maxFiles) {
      alert(`Maximum ${this.maxFiles} photos allowed. Remove a photo first.`);
      return;
    }

    const sourceIndex = this.files.findIndex(f => f.id === fileId);
    if (sourceIndex === -1) return;

    const source = this.files[sourceIndex];
    const clone = {
      file: source.file,
      dataUrl: source.dataUrl,
      id: this.generateId()
    };

    // Insert clone right after the source
    this.files.splice(sourceIndex + 1, 0, clone);

    this.renderPreviewGrid();
    this.debouncedUpdateHiddenInputs();
    this.updateTriggerButton();
    this.updateActionButton();
    this.scrollToLastItem();
  }

  /**
   * Open crop modal for a specific image
   * @param {string} fileId - ID of the file to crop
   */
  openCropModal(fileId) {
    const file = this.files.find(f => f.id === fileId);
    if (!file) return;

    // Create crop modal
    const cropModal = document.createElement('div');
    cropModal.className = 'photo-upload-crop-modal';
    cropModal.innerHTML = `
      <div class="photo-upload-crop-modal__overlay"></div>
      <div class="photo-upload-crop-modal__container">
        <div class="photo-upload-crop-modal__header">
          <h3>Crop Image</h3>
          <button type="button" class="photo-upload-crop-modal__close">&times;</button>
        </div>
        <div class="photo-upload-crop-modal__body">
          <div class="photo-upload-crop-modal__canvas-wrapper">
            <canvas class="photo-upload-crop-modal__canvas"></canvas>
            <div class="photo-upload-crop-modal__crop-area">
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--nw" data-handle="nw"></div>
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--ne" data-handle="ne"></div>
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--sw" data-handle="sw"></div>
              <div class="photo-upload-crop-modal__resize-handle photo-upload-crop-modal__resize-handle--se" data-handle="se"></div>
            </div>
          </div>
          <div class="photo-upload-crop-modal__toolbar">
            <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="flip-h" title="Flip Horizontal">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M16 3l4 4-4 4"/>
                <path d="M20 7H4"/>
                <path d="M8 21l-4-4 4-4"/>
                <path d="M4 17h16"/>
              </svg>
              <span>Flip H</span>
            </button>
            <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="flip-v" title="Flip Vertical">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M3 8l4-4 4 4"/>
                <path d="M7 4v16"/>
                <path d="M21 16l-4 4-4-4"/>
                <path d="M17 20V4"/>
              </svg>
              <span>Flip V</span>
            </button>
            <button type="button" class="photo-upload-crop-modal__tool-btn" data-tool="rotate-cw" title="Rotate 90°">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="20" height="20">
                <path d="M23 4v6h-6"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
              <span>Rotate</span>
            </button>
          </div>
        </div>
        <div class="photo-upload-crop-modal__footer">
          <button type="button" class="photo-upload-crop-modal__cancel">Cancel</button>
          <button type="button" class="photo-upload-crop-modal__apply">Apply Crop</button>
        </div>
      </div>
    `;

    document.body.appendChild(cropModal);

    // Transform state
    let flipH = false;
    let flipV = false;
    let rotation = 0; // 0, 90, 180, 270

    // Load image from original file for best quality
    const img = new Image();
    let objectUrl = null;
    if (file.file) {
      objectUrl = URL.createObjectURL(file.file);
    }

    img.onload = () => {
      const canvas = cropModal.querySelector('.photo-upload-crop-modal__canvas');
      const ctx = canvas.getContext('2d');
      const wrapper = cropModal.querySelector('.photo-upload-crop-modal__canvas-wrapper');
      const cropArea = cropModal.querySelector('.photo-upload-crop-modal__crop-area');

      // Responsive max size: larger on desktop, smaller on mobile
      const isMobile = window.innerWidth <= 767;
      const maxSize = isMobile ? 400 : 600;

      /**
       * Get effective display dimensions based on rotation
       */
      const getDisplayDims = () => {
        const isRotated = rotation === 90 || rotation === 270;
        const srcW = isRotated ? img.height : img.width;
        const srcH = isRotated ? img.width : img.height;

        let dw = srcW;
        let dh = srcH;
        if (dw > maxSize || dh > maxSize) {
          const ratio = Math.min(maxSize / dw, maxSize / dh);
          dw = Math.round(srcW * ratio);
          dh = Math.round(srcH * ratio);
        }
        return { displayWidth: dw, displayHeight: dh };
      };

      /**
       * Redraw the canvas with current transforms
       */
      const redrawCanvas = () => {
        const { displayWidth, displayHeight } = getDisplayDims();

        canvas.width = displayWidth;
        canvas.height = displayHeight;
        wrapper.style.width = displayWidth + 'px';
        wrapper.style.height = displayHeight + 'px';

        ctx.save();
        ctx.clearRect(0, 0, displayWidth, displayHeight);

        // Move to center for transforms
        ctx.translate(displayWidth / 2, displayHeight / 2);

        // Apply rotation
        ctx.rotate((rotation * Math.PI) / 180);

        // Apply flips
        ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        // Draw image centered (use original dims since rotation is handled by ctx)
        const isRotated = rotation === 90 || rotation === 270;
        const drawW = isRotated ? displayHeight : displayWidth;
        const drawH = isRotated ? displayWidth : displayHeight;
        ctx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

        ctx.restore();

        // Reset crop area to fit new dimensions
        cropSize = Math.min(displayWidth, displayHeight);
        cropX = (displayWidth - cropSize) / 2;
        cropY = (displayHeight - cropSize) / 2;
        updateCropAreaUI();
      };

      // Initial draw
      let { displayWidth, displayHeight } = getDisplayDims();
      canvas.width = displayWidth;
      canvas.height = displayHeight;
      wrapper.style.width = displayWidth + 'px';
      wrapper.style.height = displayHeight + 'px';
      ctx.drawImage(img, 0, 0, displayWidth, displayHeight);

      // Setup crop area (1:1 ratio, centered)
      const minCropSize = 50;
      let cropSize = Math.min(displayWidth, displayHeight);
      let cropX = (displayWidth - cropSize) / 2;
      let cropY = (displayHeight - cropSize) / 2;

      const updateCropAreaUI = () => {
        cropArea.style.width = cropSize + 'px';
        cropArea.style.height = cropSize + 'px';
        cropArea.style.left = cropX + 'px';
        cropArea.style.top = cropY + 'px';
      };

      updateCropAreaUI();

      // --- Toolbar events ---
      cropModal.querySelector('[data-tool="flip-h"]').addEventListener('click', () => {
        flipH = !flipH;
        redrawCanvas();
      });
      cropModal.querySelector('[data-tool="flip-v"]').addEventListener('click', () => {
        flipV = !flipV;
        redrawCanvas();
      });
      cropModal.querySelector('[data-tool="rotate-cw"]').addEventListener('click', () => {
        rotation = (rotation + 90) % 360;
        redrawCanvas();
        // Update local display dims reference
        const dims = getDisplayDims();
        displayWidth = dims.displayWidth;
        displayHeight = dims.displayHeight;
      });

      // Drag and resize state
      let isDragging = false;
      let isResizing = false;
      let resizeHandle = null;
      let startX = 0, startY = 0, startCropX = 0, startCropY = 0, startCropSize = 0;

      // Handle drag start on crop area (not on resize handles)
      const handleCropAreaDown = (e) => {
        // Normalize touch/mouse
        const point = e.touches ? e.touches[0] : e;
        if (e.target.dataset.handle) return; // Skip if clicking on resize handle

        isDragging = true;
        startX = point.clientX;
        startY = point.clientY;
        startCropX = cropX;
        startCropY = cropY;
        e.preventDefault();
      };

      cropArea.addEventListener('mousedown', handleCropAreaDown);
      cropArea.addEventListener('touchstart', handleCropAreaDown, { passive: false });

      // Handle resize start on handles
      cropModal.querySelectorAll('.photo-upload-crop-modal__resize-handle').forEach(handle => {
        const handleResizeDown = (e) => {
          const point = e.touches ? e.touches[0] : e;
          isResizing = true;
          resizeHandle = (e.target || e.currentTarget).dataset.handle;
          startX = point.clientX;
          startY = point.clientY;
          startCropX = cropX;
          startCropY = cropY;
          startCropSize = cropSize;
          e.preventDefault();
          e.stopPropagation();
        };
        handle.addEventListener('mousedown', handleResizeDown);
        handle.addEventListener('touchstart', handleResizeDown, { passive: false });
      });

      // Handle mouse/touch move for both drag and resize
      const handlePointerMove = (e) => {
        const point = e.touches ? e.touches[0] : e;
        // Use current display dimensions
        const curDims = getDisplayDims();
        const dw = curDims.displayWidth;
        const dh = curDims.displayHeight;

        if (isDragging) {
          const dx = point.clientX - startX;
          const dy = point.clientY - startY;

          cropX = Math.max(0, Math.min(dw - cropSize, startCropX + dx));
          cropY = Math.max(0, Math.min(dh - cropSize, startCropY + dy));

          updateCropAreaUI();
        } else if (isResizing) {
          const dx = point.clientX - startX;
          const dy = point.clientY - startY;

          // Use the larger delta for 1:1 ratio resize
          const delta = Math.abs(dx) > Math.abs(dy) ? dx : dy;

          let newSize, newX, newY;

          switch (resizeHandle) {
            case 'se': // Southeast - grow/shrink from bottom-right
              newSize = Math.max(minCropSize, Math.min(
                startCropSize + delta,
                dw - startCropX,
                dh - startCropY
              ));
              newX = startCropX;
              newY = startCropY;
              break;
            case 'sw': // Southwest - grow/shrink from bottom-left
              newSize = Math.max(minCropSize, Math.min(
                startCropSize - dx,
                startCropX + startCropSize,
                dh - startCropY
              ));
              newX = startCropX + startCropSize - newSize;
              newY = startCropY;
              break;
            case 'ne': // Northeast - grow/shrink from top-right
              newSize = Math.max(minCropSize, Math.min(
                startCropSize + dx,
                dw - startCropX,
                startCropY + startCropSize
              ));
              newX = startCropX;
              newY = startCropY + startCropSize - newSize;
              break;
            case 'nw': // Northwest - grow/shrink from top-left
              newSize = Math.max(minCropSize, Math.min(
                startCropSize - delta,
                startCropX + startCropSize,
                startCropY + startCropSize
              ));
              newX = startCropX + startCropSize - newSize;
              newY = startCropY + startCropSize - newSize;
              break;
            default:
              return;
          }

          // Validate bounds
          if (newX >= 0 && newY >= 0 &&
            newX + newSize <= dw &&
            newY + newSize <= dh) {
            cropSize = newSize;
            cropX = newX;
            cropY = newY;
            updateCropAreaUI();
          }
        }
      };

      const handlePointerUp = () => {
        isDragging = false;
        isResizing = false;
        resizeHandle = null;
      };

      document.addEventListener('mousemove', handlePointerMove);
      document.addEventListener('mouseup', handlePointerUp);
      document.addEventListener('touchmove', handlePointerMove, { passive: false });
      document.addEventListener('touchend', handlePointerUp);

      // Apply crop + transforms
      cropModal.querySelector('.photo-upload-crop-modal__apply').addEventListener('click', () => {
        const curDims = getDisplayDims();
        const dw = curDims.displayWidth;
        const dh = curDims.displayHeight;

        // Create a full-size transformed canvas first
        const isRotated = rotation === 90 || rotation === 270;
        const fullW = isRotated ? img.height : img.width;
        const fullH = isRotated ? img.width : img.height;

        const transformCanvas = document.createElement('canvas');
        transformCanvas.width = fullW;
        transformCanvas.height = fullH;
        const tCtx = transformCanvas.getContext('2d');

        tCtx.translate(fullW / 2, fullH / 2);
        tCtx.rotate((rotation * Math.PI) / 180);
        tCtx.scale(flipH ? -1 : 1, flipV ? -1 : 1);

        const drawW = isRotated ? fullH : fullW;
        const drawH = isRotated ? fullW : fullH;
        tCtx.drawImage(img, -drawW / 2, -drawH / 2, drawW, drawH);

        // Now crop from the transformed canvas
        const scaleX = fullW / dw;
        const scaleY = fullH / dh;

        const actualX = cropX * scaleX;
        const actualY = cropY * scaleY;
        const actualSize = cropSize * Math.max(scaleX, scaleY);

        const cropCanvas = document.createElement('canvas');
        cropCanvas.width = actualSize;
        cropCanvas.height = actualSize;
        const cropCtx = cropCanvas.getContext('2d');

        cropCtx.drawImage(
          transformCanvas,
          actualX, actualY, actualSize, actualSize,
          0, 0, actualSize, actualSize
        );

        // Re-compress the cropped image at high quality for crisp preview
        const tempImg = new Image();
        tempImg.onload = () => {
          file.dataUrl = this.compressImage(tempImg, 800, 0.85);
          cleanup();
          this.renderPreviewGrid();
          this.debouncedUpdateHiddenInputs();
        };
        tempImg.src = cropCanvas.toDataURL('image/jpeg', 0.9);
      });

      // Cleanup on close
      const cleanup = () => {
        document.removeEventListener('mousemove', handlePointerMove);
        document.removeEventListener('mouseup', handlePointerUp);
        document.removeEventListener('touchmove', handlePointerMove);
        document.removeEventListener('touchend', handlePointerUp);
        if (objectUrl) URL.revokeObjectURL(objectUrl);
        cropModal.remove();
      };

      cropModal.querySelector('.photo-upload-crop-modal__close').addEventListener('click', cleanup);
      cropModal.querySelector('.photo-upload-crop-modal__cancel').addEventListener('click', cleanup);
      cropModal.querySelector('.photo-upload-crop-modal__overlay').addEventListener('click', cleanup);
    };

    img.src = objectUrl || file.dataUrl;
  }

  /**
   * Debounced version of updateHiddenInputs.
   * Coalesces rapid calls (add/remove/duplicate) into a single DOM update.
   */
  debouncedUpdateHiddenInputs() {
    if (this._hiddenInputTimer) clearTimeout(this._hiddenInputTimer);
    this._hiddenInputTimer = setTimeout(() => this.updateHiddenInputs(), 150);
  }

  /**
   * Update hidden inputs for form submission.
   * Lightweight: only stores metadata (count + id). Photo data is compressed
   * on-demand at Add to Cart time to avoid redundant CPU work.
   */
  updateHiddenInputs() {
    const container = this.querySelector('.photo-upload-hidden-inputs');
    if (!container) return;

    let html = '';

    // Only store count + upload_id in hidden inputs.
    // Actual photo data is built at submit time in handleAddToCart.
    html += `<input type="hidden" name="properties[_photo_count]" value="${this.files.length}" />`;
    html += `<input type="hidden" name="properties[_upload_id]" value="${Date.now().toString()}" />`;

    container.innerHTML = html;

    // Sync to product form
    this.syncInputsToProductForm();
  }

  /**
   * Sync hidden inputs to the product form for AJAX submission
   */
  syncInputsToProductForm() {
    // Find the product form
    const productFormComponent = this.closest('product-form-component') ||
      document.querySelector('product-form-component');
    const productForm = productFormComponent?.querySelector('form') ||
      document.querySelector('form[action*="/cart/add"]');

    if (!productForm) return;

    // Remove existing photo inputs from form
    productForm.querySelectorAll('input[data-photo-upload="true"]').forEach(el => el.remove());

    // Clone and add new inputs to form
    const hiddenInputs = this.querySelectorAll('.photo-upload-hidden-inputs input');
    hiddenInputs.forEach(input => {
      const clone = input.cloneNode(true);
      clone.style.display = 'none';
      clone.setAttribute('data-photo-upload', 'true');
      productForm.appendChild(clone);
    });
  }

  /**
   * Update trigger button appearance
   */
  updateTriggerButton() {
    if (!this.trigger) return;

    if (this.files.length > 0) {
      this.trigger.classList.add('photo-upload-trigger--filled');
      this.trigger.querySelector('span').textContent = `${this.files.length} photo${this.files.length > 1 ? 's' : ''} selected`;
    } else {
      this.trigger.classList.remove('photo-upload-trigger--filled');
      this.trigger.querySelector('span').textContent = this.dataset.buttonText || 'Upload pictures';
    }
  }

  /**
   * Handle Add to Cart button click in modal
   * Uses FormData (multipart/form-data) for reliable submission.
   * 
   * IMPORTANT: We use FormData instead of JSON because Shopify's /cart/add.js
   * enforces strict character limits on property values when sent as JSON.
   * Base64-encoded images easily exceed these limits (~5KB per value in JSON mode).
   * FormData (multipart/form-data) does NOT have the same per-value restrictions,
   * which is exactly how the theme's own ProductFormComponent submits.
   */
  async handleAddToCart() {
    if (this.files.length === 0) {
      alert('Please select at least one photo');
      return;
    }

    if (this.isSubmitting) return;
    this.isSubmitting = true;

    // Update the button state
    const addToCartBtn = this.querySelector('.photo-upload-addtocart-btn');
    if (addToCartBtn) {
      addToCartBtn.textContent = 'ADDING...';
      addToCartBtn.disabled = true;
    }

    try {
      // Get variant ID — use multiple sources in priority order:
      // 0. Variant picker (most reliable — theme-managed, always this product)
      // 1. URL param (updated by VariantPicker via history.replaceState)
      // 2. data-variant-id attribute (set by Liquid at page load)
      let variantId = null;

      // Source 0: Variant picker checked input — always correct for this product
      const pickerVariant = this.getVariantFromPicker();
      if (pickerVariant) {
        variantId = pickerVariant;
        console.log('[PhotoUpload] Variant from picker:', variantId);
      }

      // Source 1: URL query param ?variant=xxx
      if (!variantId) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlVariant = urlParams.get('variant');
        if (urlVariant) {
          variantId = urlVariant;
          console.log('[PhotoUpload] Variant from URL:', variantId);
        }
      }

      // Source 2: data-variant-id attribute (Liquid rendered at page load)
      if (!variantId) {
        variantId = this.dataset.variantId;
        console.log('[PhotoUpload] Variant from data attribute (fallback):', variantId);
      }

      if (!variantId) {
        throw new Error('Could not determine product variant');
      }

      // Log variant source for debugging
      console.log(`[PhotoUpload] Submitting variant ${variantId} for product ${this.dataset.productId}`);

      // Build properties for order
      // When Cloudflare Worker is configured:
      //   'Photo N' = R2 public URL → clickable link visible in Shopify Admin order
      // When no Worker:
      //   'Photo N' = tiny base64 thumbnail (100x100) as fallback
      const properties = {
        '_photo_count': this.files.length.toString(),
        '_upload_id': Date.now().toString()
      };

      const workerConfig = this.getWorkerConfig();
      const useWorker = !!workerConfig;

      // ZIP filename: customer name > customer email > upload_id
      const customerName = this.dataset.customerName?.trim();
      const customerEmail = this.dataset.customerEmail?.trim();
      const rawZipName = customerName || customerEmail || properties['_upload_id'];
      // Sanitize: remove special chars, replace spaces with hyphens
      const zipName = rawZipName.replace(/[^a-zA-Z0-9\s@._-]/g, '').replace(/\s+/g, '-');

      if (useWorker) {
        console.log(`[PhotoUpload] Worker enabled. Uploading ${this.files.length} photos to ${workerConfig.url}...`);
        const uploadResults = await this.uploadAllToWorker(addToCartBtn, workerConfig);

        if (addToCartBtn) addToCartBtn.textContent = 'PROCESSING...';

        // Process Worker results — compress fallbacks in parallel
        let allSuccess = true;
        const photoUrls = [];
        const fallbackPromises = [];
        for (const result of uploadResults) {
          if (result.url) {
            properties[`Photo ${result.index + 1}`] = result.url;
            photoUrls.push(result.url);
          } else {
            allSuccess = false;
            console.warn(`[PhotoUpload] Worker failed for photo ${result.index + 1}, using base64 fallback`);
            fallbackPromises.push(
              this.compressForOrderThumbnail(this.files[result.index].dataUrl)
                .then(tinyDataUrl => { properties[`Photo ${result.index + 1}`] = tinyDataUrl; })
            );
          }
        }
        if (fallbackPromises.length > 0) await Promise.all(fallbackPromises);

        if (allSuccess) {
          console.log('[PhotoUpload] All photos uploaded to Worker R2 successfully');
        }

        // Create gallery ZIP before add-to-cart so the URL is saved in line properties
        if (photoUrls.length > 0) {
          if (addToCartBtn) addToCartBtn.textContent = 'CREATING ZIP...';
          try {
            const zipUrl = await this._createGalleryAndGetUrl(workerConfig, photoUrls, zipName);
            if (zipUrl) {
              properties['_zip_url'] = zipUrl;
              console.log('[PhotoUpload] ZIP URL stored in line properties:', zipUrl);
            }
          } catch (zipErr) {
            console.warn('[PhotoUpload] Gallery creation failed, continuing without ZIP:', zipErr);
          }
        }
      } else {
        // No Worker — compress all thumbnails in parallel
        console.log('[PhotoUpload] No Worker configured, using base64 mode');
        if (addToCartBtn) addToCartBtn.textContent = 'PROCESSING...';

        await Promise.all(
          this.files.map((item, i) =>
            this.compressForOrderThumbnail(item.dataUrl)
              .then(tinyDataUrl => { properties[`Photo ${i + 1}`] = tinyDataUrl; })
          )
        );
      }

      if (addToCartBtn) addToCartBtn.textContent = 'ADDING TO CART...';

      // Debug: log total properties size
      const totalSize = Object.values(properties).reduce((sum, v) => sum + v.length, 0);
      console.log(`[PhotoUpload] Cart properties total size: ${(totalSize / 1024).toFixed(1)}KB for ${this.files.length} photos`);

      // Use JSON encoding — most reliable for Shopify's /cart/add.js
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId),
          quantity: 1,
          properties: properties
        })
      });

      // Parse response safely
      const responseText = await response.text();
      let result;
      try {
        result = JSON.parse(responseText);
      } catch (parseError) {
        console.error('[PhotoUpload] Response was not valid JSON:', response.status, responseText.substring(0, 500));
        throw new Error(
          response.status === 422 ? 'Properties too large. Try fewer photos.'
            : !response.ok ? `Server error (${response.status}). Please try again.`
              : 'Unexpected response from server.'
        );
      }

      // Shopify returns { status: 422, message: '...' } on error
      if (result.status && result.status >= 400) {
        throw new Error(result.description || result.message || 'Failed to add to cart');
      }

      if (!response.ok) {
        throw new Error(result.description || result.message || 'Failed to add to cart');
      }

      // Close modal
      this.handleModalClose();

      // Remember file count before clearing
      const addedCount = this.files.length;

      // Clear files after successful add
      this.files = [];
      this.updateHiddenInputs();
      this.updateTriggerButton();

      // Update cart count in the header
      this.updateCartBubble();



      // Dispatch cart update event for theme integration
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: result }));

      // Show success popup with the number of items just added
      this.showSuccessPopup(addedCount);

    } catch (error) {
      console.error('Error adding to cart:', error);
      alert(error.message || 'Failed to add to cart. Please try again.');
    } finally {
      this.isSubmitting = false;
      if (addToCartBtn) {
        addToCartBtn.textContent = 'ADD TO CART';
        addToCartBtn.disabled = false;
      }
    }
  }

  /**
   * Update cart bubble/count in the header
   */
  async updateCartBubble() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      // Try common selectors for cart count
      const cartBubbles = document.querySelectorAll(
        '.cart-bubble-count, [data-cart-count], .cart-count, .cart-item-count, cart-bubble-component'
      );

      cartBubbles.forEach(el => {
        if (el.tagName.toLowerCase() === 'cart-bubble-component') {
          // Dispatch event for custom component
          el.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));
        } else {
          el.textContent = cart.item_count;
        }
      });

      // Also dispatch a global event for the theme
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart }));

      // Try refreshing cart sections via Section Render API
      const sectionsToRefresh = document.querySelectorAll('[data-section-id]');
      if (sectionsToRefresh.length > 0) {
        // Use Shopify section rendering API to refresh cart-related sections
        const sectionIds = [];
        sectionsToRefresh.forEach(el => {
          const id = el.dataset.sectionId;
          if (id && el.closest('.cart-items-component, cart-bubble-component, [data-cart-section]')) {
            sectionIds.push(id);
          }
        });
      }
    } catch (e) {
      console.warn('Could not update cart bubble:', e);
    }
  }

  /**
   * Show success popup after add to cart
   * @param {number} addedCount - Number of items just added
   */
  async showSuccessPopup(addedCount = 1) {
    // Remove any existing success popups
    document.querySelectorAll('.photo-upload-success').forEach(el => el.remove());

    // Fetch current cart item count
    let cartItemCount = addedCount;
    try {
      const res = await fetch('/cart.js');
      const cart = await res.json();
      cartItemCount = cart.item_count || addedCount;
    } catch (e) {
      console.warn('[PhotoUpload] Could not fetch cart count for popup:', e);
    }

    const popup = document.createElement('div');
    popup.className = 'photo-upload-success';
    popup.innerHTML = `
      <button type="button" class="photo-upload-success__close">&times;</button>
      <p class="photo-upload-success__message">
        Product has been successfully added to the shopping cart
      </p>
      <div class="photo-upload-success__buttons">
        <a href="/cart" class="photo-upload-success__btn photo-upload-success__btn--secondary">
          View your cart ( ${cartItemCount} ${cartItemCount === 1 ? 'item' : 'items'} added)
        </a>
        <a href="/checkout" class="photo-upload-success__btn photo-upload-success__btn--primary">
          SECURE CHECKOUT
        </a>
      </div>
      <a href="#" class="photo-upload-success__more-link" onclick="this.closest('.photo-upload-success').remove(); return false;">
        GET MORE MAGNETS
      </a>
      <div class="photo-upload-success__consent">
        <label class="photo-upload-toggle">
          <input type="checkbox" />
          <span class="photo-upload-toggle__slider"></span>
        </label>
        <span>I agree to have my order featured in your social media content.</span>
      </div>
    `;

    document.body.appendChild(popup);

    // Close button
    popup.querySelector('.photo-upload-success__close')?.addEventListener('click', () => {
      popup.remove();
    });

    // Auto-remove after 10 seconds
    setTimeout(() => {
      popup.remove();
    }, 10000);
  }

  /**
   * Create gallery ZIP and return the URL.
   * Called BEFORE add-to-cart so the ZIP URL can be stored in line properties.
   * @param {{ url: string, key: string }} workerConfig - Worker config
   * @param {string[]} photoUrls - Array of R2 photo URLs
   * @param {string} uploadId - Unique upload ID to use as ZIP filename
   * @returns {Promise<string|null>} ZIP URL or null on failure
   */
  async _createGalleryAndGetUrl(workerConfig, photoUrls, uploadId) {
    try {
      const galleryRes = await fetch(`${workerConfig.url}/gallery`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-API-Key': workerConfig.key,
        },
        body: JSON.stringify({ photoUrls, zipName: uploadId }),
      });
      const galleryResult = await galleryRes.json();

      const noteUrl = galleryResult.zipUrl || galleryResult.galleryUrl;
      if (noteUrl) {
        console.log('[PhotoUpload] Gallery ZIP created:', noteUrl);
        return noteUrl;
      }

      console.warn('[PhotoUpload] Gallery response missing URL:', galleryResult);
      return null;
    } catch (err) {
      console.warn('[PhotoUpload] Gallery creation failed:', err);
      return null;
    }
  }

  /**
   * Get files for external access
   */
  getFiles() {
    return this.files;
  }

  /**
   * Clear all files
   */
  clearFiles() {
    this.files = [];
    this.updateHiddenInputs();
    this.updateTriggerButton();
    this.updateActionButton();
    this.showStep('upload');
  }
}

// Register custom element
if (!customElements.get('photo-upload-component')) {
  customElements.define('photo-upload-component', PhotoUploadComponent);
}
